import { buildQueryString } from '@izimi/utils';

import type FetchService from '../FetchService';
import type {
  ApiAcceptedLanguage,
  ApiComponentType,
  ApiRequestHeaderParamType,
  ApiRequestPathParamType,
  ApiRequestQueryParamType,
} from '../types';
import type { ApiNabanAuthenticationModes, ApiNabanDeed, ApiNabanDeeds } from './types';

class NabanApi {
  protected BASE_URL = '/naban';

  constructor(protected fetchService: FetchService) {}

  getNabanAuthenticationModes() {
    return this.fetchService.get<ApiNabanAuthenticationModes>(
      `${this.BASE_URL}/authentication-modes`,
    );
  }

  getNabanDeeds({
    authenticationModeToken,
    language,
    ...params
  }: {
    authenticationModeToken: string;
    offset: number;
    limit: 5 | 10 | 20 | 30 | 50 | 100;
    language?: ApiAcceptedLanguage;
  }) {
    const qs = buildQueryString(params);
    return this.fetchService.get<ApiNabanDeeds>(
      `${this.BASE_URL}/authentication-modes/${authenticationModeToken}/deeds${qs}`,
      undefined,
      language && {
        'Accept-Language': language,
      },
    );
  }

  getNabanDeed({
    authenticationModeToken,
    napId,
    language,
  }: ApiRequestPathParamType<'getNabanDeedById'> & { language?: ApiAcceptedLanguage }) {
    return this.fetchService.get<ApiNabanDeed>(
      `${this.BASE_URL}/authentication-modes/${authenticationModeToken}/deeds/${napId}`,
      undefined,
      language && {
        'Accept-Language': language,
      },
    );
  }

  copyNabanDeedDocument(values: ApiComponentType<'NabanCopyRequest'>) {
    return this.fetchService.post(`${this.BASE_URL}/deeds/copy`, values);
  }

  downloadNabanDeedDocument({
    authenticationModeToken,
    documentIdentifier,
    napId,
    embeddedDocument,
    preview,
  }: ApiRequestPathParamType<'getDeedDocumentByIdentifier'> &
    ApiRequestQueryParamType<'getDeedDocumentByIdentifier'>) {
    const qs = buildQueryString({ embeddedDocument, preview });
    const client: ApiRequestHeaderParamType<'getDeedDocumentByIdentifier'>['X-UI-Client'] =
      this.fetchService.appSpec.app === 'web-citizen-portal' ? 'portal' : 'izimi';
    return this.fetchService.get<Blob>(
      `${this.BASE_URL}/authentication-modes/${authenticationModeToken}/deeds/${napId}/documents/${documentIdentifier}${qs}`,
      { resAsBlob: true },
      { 'X-UI-Client': client },
    );
  }
}

export default NabanApi;
